<template>
  <Sidebar
    app-name="Campus"
    :navigation-links="launchpadNavLinks"
  >
  </Sidebar>
</template>

<script setup lang="ts">
import { inject, computed, type Component, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { Sidebar } from '@outtalent-dev/shared'
import { useAuth } from '@/composables/useAuth'
import { useGuideMode } from '@/composables/useGuideMode'
import type { RouteLocationRaw } from 'vue-router'
import {
  Home,
  ArrowLeft,
  BookOpen,
  LayoutGrid,
  Library,
  ListTodo,
  List,
} from 'lucide-vue-next'

interface SidebarNavLink {
  name: string
  icon: Component
  to: string | RouteLocationRaw
  active: boolean
}

const route = useRoute()
const userStore = useUserStore()
const { isGuide } = useAuth()
const { guideQuery, isGuideMode, backToFellowUrl } = useGuideMode()
const _isSidebarCollapsed = inject<Ref<boolean>>('isSidebarCollapsed')

type LaunchpadLinkDef = {
  name: string
  icon: Component
  path: string
  routeNames: string[]
  hideForGuides: boolean
}

const allLaunchpadLinks: LaunchpadLinkDef[] = [
  { name: 'Home', icon: Home, path: '/home', routeNames: ['PersonalizedHome'], hideForGuides: false },
  { name: 'My Tasks', icon: ListTodo, path: '/curriculum/tasks', routeNames: ['PersonalizedCurriculum'], hideForGuides: false },
  { name: 'All Tasks', icon: List, path: '/curriculum/tasks/list', routeNames: ['TaskList'], hideForGuides: false },
  { name: 'Board', icon: LayoutGrid, path: '/board', routeNames: ['TaskBoard'], hideForGuides: false },
  {
    name: 'Curriculum',
    icon: BookOpen,
    path: '/curriculum/plan',
    routeNames: ['CurriculumPlan'],
    hideForGuides: false,
  },
  {
    name: 'Configure Tracks',
    icon: Library,
    path: '/tracks',
    routeNames: ['AllTracks', 'TrackDetail'],
    hideForGuides: false,
  },
]

/** Preserve ?userId=&podId=&fellowName= when a guide edits a fellow’s campus views */
const launchpadNavLinks = computed<SidebarNavLink[]>(() => {
  const filtered = isGuide.value ? allLaunchpadLinks.filter((l) => !l.hideForGuides) : allLaunchpadLinks
  const q = guideQuery.value
  const withGuideContext = Object.keys(q).length > 0
  return filtered.map((link) => {
    if (link.path === '/home' && isGuideMode.value) {
      return {
        name: 'Go back',
        icon: ArrowLeft,
        to: backToFellowUrl.value,
        active: false,
      }
    }
    const to: RouteLocationRaw = withGuideContext ? { path: link.path, query: { ...q } } : link.path
    const name =
      link.path === '/curriculum/tasks' && isGuideMode.value ? 'Fellows tasks' : link.name
    return {
      name,
      icon: link.icon,
      to,
      active: link.routeNames.includes(route.name as string),
    }
  })
})

const _userName = computed(() => {
  const u = userStore.currentUser
  if (!u) return ''
  const first = (u.first_name ?? '').trim()
  const last = (u.last_name ?? '').trim()
  return [first, last].filter(Boolean).join(' ') || u.email || 'User'
})
</script>
